import React, { useState, useEffect } from "react";
import {
  CalculatorService,
  type CalculatorLevel,
  type SubmissionResults,
} from "../../services/calculatorService";
import { Button } from "../ui/Button";
import { LockSubmitModal, type LockSubmitModalRef } from "./LockSubmitModal";
import { cx } from "../../utils/functions";

interface FormData {
  [rowId: string]: { [columnId: string]: string };
}

interface DataStorageAssessmentProps {
  labId: string;
  level: CalculatorLevel;
  initialData: FormData;
  locked: boolean;
  onSubmissionSuccess: (results: SubmissionResults) => void;
  onSubmissionError: (error: string) => void;
}

const STORAGE_TYPES = [
  "Personal Device Storage",
  "Organisational Network Drive",
  "Cluster Scratch Space",
  "Long-Term Storage",
];

const STORAGE_EXAMPLES: Record<string, string> = {
  "Personal Device Storage":
    "Desktop, Laptop, Personal Device, Workstation",
  "Organisational Network Drive":
    "Shared Drive, Network Drive, S Drive, H Drive",
  "Cluster Scratch Space": "HPC, Scratch Space, Temporary Storage",
  "Long-Term Storage": "Object Storage, Tape Archive, Cold Storage",
};

const COLUMNS = [
  {
    id: "users",
    label: "Number of users",
    type: "number" as const,
    min: 0,
    max: 10000,
    step: 1,
    helpText:
      "Approximate number of users regularly storing data in this location.",
  },
  {
    id: "avgWeeklyTb",
    label: "Avg weekly TB across lab",
    type: "number" as const,
    min: 0.1,
    max: 2000,
    step: 0.1,
    helpText:
      "Average amount of data written to this storage per week across the lab (in TB).",
  },
  {
    id: "oldestFiles",
    label: "Approximate age of oldest files",
    type: "dropdown" as const,
    options: ["<1 year", "1–3 years", "3–5 years", "5–10 years", ">10 years"],
    helpText: "Age of the oldest data retained in this storage space.",
  },
  {
    id: "autoArchive",
    label: "Is automated archiving in place?",
    type: "dropdown" as const,
    options: [
      "Fully automated",
      "Partially automated",
      "Manual",
      "Not in place",
      "Unsure",
    ],
    helpText:
      "Whether data is automatically moved to lower-energy or long-term storage.",
  },
];

export const DataStorageAssessment: React.FC<DataStorageAssessmentProps> = ({
  labId,
  level,
  initialData,
  locked,
  onSubmissionSuccess,
  onSubmissionError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const lockSubmitModalRef = React.useRef<LockSubmitModalRef>(null);

  // Example state for demo/mock as requested
  useEffect(() => {
    if (Object.keys(initialData).length === 0) {
      const exampleData: FormData = {};
      setFormData(exampleData);
    }
  }, [initialData]);

  useEffect(() => {
    // Any interaction is progress
    setCanSubmit(Object.keys(formData).length > 0);
  }, [formData]);

  const handleFieldChange = (
    rowLabel: string,
    columnId: string,
    value: string,
  ) => {
    if (locked) return;

    setFormData((prev) => ({
      ...prev,
      [rowLabel]: {
        ...(prev[rowLabel] || {}),
        [columnId]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await CalculatorService.submitCalculator(
        labId,
        "Data Storage",
        level,
      );
      onSubmissionSuccess(response);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit assessment";
      onSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 w-full">
      <div className="overflow-x-auto pb-12 -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="table table-bordered w-full border-collapse bg-white shadow-sm rounded-lg">
          <thead>
            {/* whitespace-normal overrides daisyui's `thead { white-space:
                nowrap }`. Without it every header is pinned to one line, which
                is the only reason this table needed a min-w-[950px] floor. */}
            <tr className="bg-base-200">
              <th className="px-4 py-3 text-left border border-gray-300 whitespace-normal">
                Storage Type
              </th>
              <th className="px-4 py-3 text-left border border-gray-300 min-w-56 whitespace-normal">
                Example
              </th>
              {COLUMNS.map((col, index) => (
                <th
                  key={col.id}
                  className="px-2 py-2 text-left border border-gray-300 min-w-32 whitespace-normal"
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 leading-tight break-words">
                      {col.label}
                    </span>
                    <span
                      className={cx(
                        "tooltip tooltip-custom cursor-help flex items-center z-50",
                        index === COLUMNS.length - 1
                          ? "tooltip-left"
                          : "tooltip-bottom",
                      )}
                      data-tip={col.helpText}
                    >
                      <svg
                        className="w-4 h-4 text-info shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STORAGE_TYPES.map((rowLabel) => (
              <tr key={rowLabel} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 font-medium border border-gray-300 bg-gray-50 sticky left-0 z-10 text-sm">
                  {rowLabel}
                </td>
                <td className="px-3 py-2 text-sm border border-gray-300 bg-gray-50 min-w-[220px]">
                  {STORAGE_EXAMPLES[rowLabel]}
                </td>
                {COLUMNS.map((col) => {
                  const value = formData[rowLabel]?.[col.id] || "";

                  return (
                    <td
                      key={col.id}
                      className="p-1.5 border border-gray-300 align-middle"
                    >
                      <div className="flex justify-center items-center min-h-[40px]">
                        {col.type === "number" ? (
                          <input
                            type="number"
                            className="input input-bordered input-sm w-full bg-white"
                            value={value}
                            min={col.min}
                            max={col.max}
                            step={col.step}
                            placeholder="0"
                            onChange={(e) =>
                              handleFieldChange(
                                rowLabel,
                                col.id,
                                e.target.value,
                              )
                            }
                            onWheel={(e) =>
                              (e.target as HTMLInputElement).blur()
                            }
                          />
                        ) : (
                          <div className="relative w-full px-2">
                            <select
                              className="appearance-none w-full bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-100 transition-all cursor-pointer pr-10"
                              value={value}
                              onChange={(e) =>
                                handleFieldChange(
                                  rowLabel,
                                  col.id,
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select...</option>
                              {col.options?.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          variant="primary"
          onClick={() => lockSubmitModalRef.current?.showModal()}
          disabled={!canSubmit || isSubmitting || locked}
        >
          {isSubmitting ? (
            <>
              <span className="loading loading-spinner loading-sm mr-2"></span>
              Submitting...
            </>
          ) : (
            "Submit Assessment"
          )}
        </Button>
      </div>

      <LockSubmitModal
        ref={lockSubmitModalRef}
        onConfirm={handleSubmit}
        loading={isSubmitting}
      />
    </div>
  );
};
